// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListings.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';
import WishlistButton from '../WishlistButton/WishlistButton';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * Derives up to maxTags display strings from listing publicData + config categories.
 * Resolves each selected level using categoryConfiguration.key (e.g. categoryLevel → categoryLevel1)
 * and nested subcategories, matching EditListingWizard category storage.
 */
const deriveTags = (publicData, config, maxTags = 3) => {
  if (!publicData) return [];

  if (Array.isArray(publicData.tags) && publicData.tags.length > 0) {
    return publicData.tags.slice(0, maxTags).map(t => String(t).toUpperCase());
  }

  const categoryConfiguration = config?.categoryConfiguration;
  const rootCategories = categoryConfiguration?.categories || [];
  const prefix =
    categoryConfiguration && categoryConfiguration.key != null
      ? String(categoryConfiguration.key)
      : 'categoryLevel';

  const labels = [];
  let options = rootCategories;

  for (let level = 1; level <= 10 && labels.length < maxTags; level += 1) {
    const id = publicData[`${prefix}${level}`];
    if (!id) {
      break;
    }
    const node = options.find(c => c.id === id);
    if (node?.name) {
      labels.push(String(node.name).toUpperCase());
      options = node.subcategories || [];
    } else {
      labels.push(
        String(id)
          .toUpperCase()
          .replace(/-/g, ' ')
      );
      break;
    }
  }

  return labels.slice(0, maxTags);
};

/** Shopping bag icon for the Add to Bag CTA */
const BagIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2c2a26"
    strokeWidth="1.5"
  >
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

/**
 * ListingCardImage
 * Renders the listing photo (lazy-loaded) or a styled placeholder.
 * @component
 */
const ListingCardImage = props => {
  const {
    listing,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper className={aspectRatioClass} width={aspectWidth} height={aspectHeight}>
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * Editorial product card in the Wedly luxury aesthetic.
 * Displays: badge · wishlist toggle · photo · vendor · title ·
 *           categories (tags) · description · price + star rating · Add to Bag · View Details.
 *
 * @component
 * @param {Object}    props
 * @param {string?}   props.className
 * @param {string?}   props.rootClassName        overrides css.root
 * @param {string?}   props.aspectRatioClassName custom class for AspectRatioWrapper
 * @param {Object}    props.listing              API listing entity
 * @param {string?}   props.renderSizes          img/srcset size hint
 * @param {Function?} props.setActiveListing     called on hover for map highlight
 * @param {boolean?}  props.showAuthorInfo       default true
 * @param {Function?} props.onAddToBag           optional; if omitted "Add to Bag" links to listing
 * @returns {JSX.Element}
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
    onAddToBag,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    showSale,
    formattedCompareAt,
    showRatingRow,
    roundedStarRating,
    reviewCount,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', description = '', publicData } = listing?.attributes || {};
  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const slug = createSlug(title);

  const { listingType, cardStyle, cardBadge } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const cardAspectHeight = aspectHeight * 1.2;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : {};

  const tags = deriveTags(publicData, config);
  /** Plain text for card body; height is fixed in CSS (line-clamp) so truncation is optional. */
  const descriptionPlain =
    typeof description === 'string' ? description.replace(/\s+/g, ' ').trim() : '';

  const listingParams = { id, slug };

  return (
    <article className={classes} {...setActivePropsMaybe}>
      {/* ── Image area (full link) ──────────────────────────────────────── */}
      <NamedLink
        className={css.imageLink}
        name="ListingPage"
        params={listingParams}
        ariaLabel={cardAriaLabel}
      >
        <div className={css.imageWrap}>
          {showListingImage ? (
            <ListingCardImage
              renderSizes={renderSizes}
              title={titlePlain}
              listing={listing}
              aspectWidth={aspectWidth}
              aspectHeight={cardAspectHeight}
              variantPrefix={variantPrefix}
              aspectRatioClassName={aspectRatioClassName}
              lazyLoadImage={lazyLoadImage}
            />
          ) : (
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={title}
              className={aspectRatioClassName}
              width={aspectWidth}
              height={cardAspectHeight}
            />
          )}
          {cardBadge ? (
            <span className={css.badge} aria-hidden="true">
              {String(cardBadge).toUpperCase()}
            </span>
          ) : null}
        </div>
      </NamedLink>

      {/* ── Wishlist toggle (Firebase) ──────────────────────────────────── */}
      <WishlistButton
        listing={listing}
        className={css.wishlistBtn}
        allowAnonymous
        requireAuth={false}
      />

      {/* ── Card body ──────────────────────────────────────────────────── */}
      <div className={css.info}>
        {showAuthorInfo && authorDisplayName ? (
          <p className={css.vendor}>{authorDisplayName.toUpperCase()}</p>
        ) : null}

        <NamedLink className={css.titleLink} name="ListingPage" params={listingParams}>
          <h3 className={css.title}>{titlePlain}</h3>
        </NamedLink>

        <div className={css.tagsRow}>
          <div className={css.tags}>
            {tags.map((tag, i) => (
              <span key={`${tag}-${i}`} className={css.tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className={css.descriptionRow}>
          <p className={css.description}>{descriptionPlain}</p>
        </div>

        <div className={css.divider} />

        {/* Price + star rating */}
        <div className={css.priceStarsRow}>
          {showPrice ? (
            <div className={css.priceBlock} title={priceTooltip}>
              {showSale && formattedCompareAt ? (
                <span className={css.compareAtPrice}>{formattedCompareAt}</span>
              ) : null}
              <div className={css.priceRow}>
                <span className={css.currentPriceGroup}>{priceMessage}</span>
              </div>
            </div>
          ) : null}
          {showRatingRow ? (
            <div className={css.starRow}>
              <div className={css.stars}>
                {[1, 2, 3, 4, 5].map(s => (
                  <span
                    key={s}
                    className={classNames(css.star, { [css.starFilled]: s <= roundedStarRating })}
                    aria-hidden="true"
                  >
                    ★
                  </span>
                ))}
              </div>
              {reviewCount != null && reviewCount > 0 ? (
                <span className={css.reviewCount}>
                  <FormattedMessage id="ListingCard.reviewCount" values={{ count: reviewCount }} />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Primary CTA */}
        {onAddToBag ? (
          <button className={css.addToBagBtn} type="button" onClick={onAddToBag}>
            <BagIcon />
            <FormattedMessage id="ListingCard.addToBag" />
          </button>
        ) : (
          <NamedLink className={css.addToBagBtn} name="ListingPage" params={listingParams}>
            <BagIcon />
            <FormattedMessage id="ListingCard.addToBag" />
          </NamedLink>
        )}

        {/* Secondary CTA */}
        <NamedLink className={css.viewDetailsBtn} name="ListingPage" params={listingParams}>
          <FormattedMessage id="ListingCard.viewDetails" />
        </NamedLink>
      </div>
    </article>
  );
};

export default ListingCard;
